/**
 * LearnFixations.jsx — progressive fixation drill. Same phrase-chunk
 * flow as Chunking, but with visible fixation dots showing where the
 * reader's eye should land per chunk. Number of allowed fixations
 * decreases as the user's adjusted WPM climbs (level-gated), forcing
 * wider single-glance intake tied to actual chunk recognition rather
 * than raw peripheral vision.
 *
 * Live +/- pace adjuster included so users can nudge the beat while
 * running — same UX as Chunking / Pacer.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Play, Pause, RotateCcw, ArrowLeft, ChevronDown } from 'lucide-react';
import { authFetch } from '../utils/authUtils';
import { readCustomPassage } from '../utils/learnCustomPassage';
import LearnQuiz from '../components/LearnQuiz';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LearnFixations = () => {
  const navigate = useNavigate();
  const [passages, setPassages] = useState([]);
  const [passageIdx, setPassageIdx] = useState(0);
  const [wpm, setWpm] = useState(300);
  const [chunkSize, setChunkSize] = useState(2);
  const [maxChunk, setMaxChunk] = useState(2);
  const [state, setState] = useState('setup'); // setup|running|paused|quiz|done
  const [chunkIdx, setChunkIdx] = useState(0);
  const startedAt = useRef(0);
  const durationRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const custom = readCustomPassage();
    if (custom) setPassages([custom]);
    (async () => {
      try {
        if (!custom) {
          const pr = await authFetch(`${BACKEND_URL}/api/learn/passages`);
          if (pr.ok) { const d = await pr.json(); setPassages(d.passages || []); }
        }
        const gp = await authFetch(`${BACKEND_URL}/api/learn/progress`);
        if (gp.ok) { const p = await gp.json(); setMaxChunk(p.chunk_size_unlocked || 2); }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const passage = passages[passageIdx];
  const chunks = React.useMemo(() => {
    if (!passage) return [];
    const words = passage.text.split(/\s+/).filter(Boolean);
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      out.push(words.slice(i, i + chunkSize).join(' '));
    }
    return out;
  }, [passage, chunkSize]);

  const intervalMs = React.useMemo(() => Math.max(120, Math.round(60000 / (wpm / chunkSize))), [wpm, chunkSize]);

  useEffect(() => {
    if (state !== 'running') return;
    timerRef.current = setInterval(() => {
      setChunkIdx((i) => {
        if (i + 1 >= chunks.length) {
          durationRef.current = Date.now() - startedAt.current;
          clearInterval(timerRef.current);
          setState('quiz');
          return i;
        }
        return i + 1;
      });
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [state, intervalMs, chunks.length]);

  const start = () => { setChunkIdx(0); startedAt.current = Date.now(); setState('running'); };
  const pause = () => { durationRef.current += Date.now() - startedAt.current; setState('paused'); clearInterval(timerRef.current); };
  const resume = () => { startedAt.current = Date.now(); setState('running'); };
  const reset = () => { setState('setup'); setChunkIdx(0); durationRef.current = 0; clearInterval(timerRef.current); };

  if (!passage) {
    return (
      <main className="sharp-bg min-h-screen flex items-center justify-center text-white/60" data-testid="learn-fixations-loading">
        Loading passages…
      </main>
    );
  }

  // Fixation-dot count = number of stop-points in the current chunk.
  // Starts at chunkSize (one dot per word) at chunkSize=2, drops to
  // ⌈chunkSize/2⌉ once chunkSize ≥ 3 to force wider single-glance
  // intake as the reader levels up.
  const dotCount = chunkSize <= 2 ? chunkSize : Math.ceil(chunkSize / 2);

  return (
    <main className="sharp-bg min-h-screen max-w-3xl mx-auto px-4 py-6 pb-32" data-testid="learn-fixations-page">
      <button onClick={() => navigate('/learn')} className="text-xs text-white/50 hover:text-white/80 mb-4 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to LEARN
      </button>

      <header className="mb-6 flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-rose-300" />
        <div>
          <p className="eyebrow text-rose-300 mb-0.5">Drill · Progressive fixations</p>
          <h1 className="font-display text-3xl font-black text-white tracking-tight leading-none">
            One glance, wider bite<span className="text-rose-300">.</span>
          </h1>
        </div>
      </header>

      {state === 'setup' && (
        <div className="space-y-5">
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Passage</p>
            <div className="relative">
              <select
                value={passageIdx}
                onChange={(e) => setPassageIdx(Number(e.target.value))}
                className="w-full appearance-none bg-black/40 border border-white/15 rounded-lg pl-3 pr-9 py-2.5 text-sm text-white focus:outline-none focus:border-rose-400/60"
                data-testid="learn-fixations-passage-select"
              >
                {passages.map((p, i) => (<option key={p.passage_id} value={i}>{p.title} — {p.author} ({p.word_count} words)</option>))}
              </select>
              <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            </div>
          </div>
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-3">Chunk width (also = fixation frame)</p>
            <div className="grid grid-cols-4 gap-2">
              {[2, 3, 4, 5].map((n) => {
                const locked = n > maxChunk;
                return (
                  <button
                    key={n}
                    disabled={locked}
                    onClick={() => setChunkSize(n)}
                    className={`px-3 py-2.5 rounded-lg border text-sm font-black transition-colors ${
                      chunkSize === n
                        ? 'bg-rose-500 border-rose-400 text-white'
                        : locked
                          ? 'bg-white/[0.02] border-white/10 text-white/25 cursor-not-allowed'
                          : 'bg-white/[0.03] border-white/15 text-white/80 hover:bg-white/[0.08]'
                    }`}
                    data-testid={`learn-fixations-size-${n}`}
                  >
                    {n}-word{locked ? ' 🔒' : ''}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="rounded-2xl bg-[#12131A] border border-white/10 p-5">
            <p className="eyebrow text-white/50 mb-2">Pace (WPM)</p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={150}
                max={600}
                step={10}
                value={wpm}
                onChange={(e) => setWpm(Number(e.target.value))}
                className="flex-1 accent-rose-400"
                data-testid="learn-fixations-wpm-slider"
              />
              <span className="w-16 text-right font-display text-2xl font-black text-rose-300 tabular-nums">{wpm}</span>
            </div>
          </div>
          <button
            onClick={start}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-white px-4 py-3.5 text-sm font-black tracking-tight transition-colors"
            data-testid="learn-fixations-start"
          >
            <Play className="w-4 h-4" /> Start drill
          </button>
        </div>
      )}

      {(state === 'running' || state === 'paused') && (
        <div className="space-y-5">
          <div className="min-h-[45vh] flex flex-col items-center justify-center rounded-2xl bg-gradient-to-br from-slate-900 via-slate-950 to-black border border-rose-500/20 p-8 gap-6" data-testid="learn-fixations-stage">
            {/* Fixation dots — the eye should land on each dot once
                across the current chunk. Fewer dots than words means
                the reader has to widen intake. */}
            <div className="flex items-center gap-6" data-testid="learn-fixations-dots">
              {Array.from({ length: dotCount }).map((_, di) => (
                <span key={di} className="w-3 h-3 rounded-full bg-rose-400/80 shadow-[0_0_10px_2px_rgba(244,63,94,0.6)]" />
              ))}
            </div>
            <p className="font-display text-4xl sm:text-6xl font-black text-white tracking-tight text-center leading-tight" data-testid="learn-fixations-chunk">
              {chunks[chunkIdx] || '…'}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {state === 'running' ? (
              <button onClick={pause} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-white/10 hover:bg-white/15 text-white px-4 py-2.5 text-sm font-black" data-testid="learn-fixations-pause">
                <Pause className="w-4 h-4" /> Pause
              </button>
            ) : (
              <button onClick={resume} className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-rose-500 hover:bg-rose-400 text-white px-4 py-2.5 text-sm font-black" data-testid="learn-fixations-resume">
                <Play className="w-4 h-4" /> Resume
              </button>
            )}
            <button onClick={reset} className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-2.5 text-sm font-black" data-testid="learn-fixations-reset">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-2 rounded-xl bg-black/30 border border-rose-500/20 px-3 py-2">
            <span className="text-[10px] uppercase tracking-widest text-rose-300/80 font-black">Pace</span>
            <button onClick={() => setWpm((w) => Math.max(150, w - 10))} className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none" data-testid="learn-fixations-wpm-minus">−</button>
            <span className="flex-1 text-center font-display text-2xl font-black text-rose-300 tabular-nums" data-testid="learn-fixations-wpm-live">{wpm}</span>
            <button onClick={() => setWpm((w) => Math.min(600, w + 10))} className="w-9 h-9 rounded-lg bg-white/[0.05] hover:bg-white/[0.12] border border-white/10 text-white text-lg font-black leading-none" data-testid="learn-fixations-wpm-plus">+</button>
            <span className="text-[10px] uppercase tracking-widest text-white/40">WPM</span>
          </div>

          <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full bg-rose-400 transition-all" style={{ width: `${chunks.length ? Math.round(100 * (chunkIdx + 1) / chunks.length) : 0}%` }} data-testid="learn-fixations-progress" />
          </div>
        </div>
      )}

      {state === 'quiz' && (
        <LearnQuiz
          passage={passage}
          drillKind="fixations"
          durationMs={durationRef.current}
          chunkSize={chunkSize}
          metadata={{ fixation_dots: dotCount }}
          onDone={() => setState('done')}
        />
      )}

      {state === 'done' && (
        <div className="mt-5 flex gap-3">
          <button onClick={reset} className="flex-1 rounded-lg bg-rose-500 hover:bg-rose-400 text-white px-4 py-3 text-sm font-black" data-testid="learn-fixations-again">
            Run another drill
          </button>
          <button onClick={() => navigate('/learn')} className="rounded-lg bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 text-white/70 px-4 py-3 text-sm font-black">
            Back to LEARN
          </button>
        </div>
      )}
    </main>
  );
};

export default LearnFixations;
